import { Button, ButtonGroup, Checkbox, Intent } from "@blueprintjs/core";
import { IconNames } from "@blueprintjs/icons";
import { IDataTableColumn } from "nsitools-react";
import * as React from "react";

import {
  DashboardApi,
  DashboardTaskGridDto,
  EDepartment,
  ERequestType,
  RequestApi,
  UnassignedRequestApi,
} from "../../../api";
import { ExportButton, SearchTablePage } from "../../../components";
import { useDialog, useEventsContext, useGlobalData } from "../../../contexts";
import { useApiService, useTl } from "../../../hooks";
import { ETLCodes } from "../../../locales";
import { getRouteStr } from "../../../utils/RequestRouteUtil";

export interface IUnassignedRequestListProps {
  departementCode?: EDepartment;
}

export const UnassignedRequestList: React.FunctionComponent<IUnassignedRequestListProps> = ({ departementCode }) => {
  const api = useApiService(DashboardApi);
  const requestApi = useApiService(RequestApi);
  const unassignedRequestApi = useApiService(UnassignedRequestApi);
  const { t } = useTl();
  const { currentLanguage } = useGlobalData();
  const { showDialog } = useDialog();
  const [sObjState, setsObjState] = React.useState<any>();
  const selectionList = React.useMemo(() => [], []);
  const closableIdsList = React.useMemo(() => [], []);
  const [selectAll, setselectAll] = React.useState(false);
  const { dispatchEvent } = useEventsContext();

  const search = React.useCallback(
    (sObj) => {
      sObj.departementCode = departementCode;
      sObj.languageId = currentLanguage.languageId;
      setsObjState(sObj);
      return api.baseSearch(sObj);
    },
    [api, currentLanguage.languageId, departementCode]
  );
  const toggleSelectAll = React.useCallback(() => {
    const value = !selectAll;
    if (value) {
      selectionList.splice(0, selectionList.length, ...closableIdsList);
    } else {
      selectionList.length = 0;
    }
    setselectAll(value);
  }, [closableIdsList, selectAll, selectionList]);

  const columnCheckBox = React.useCallback(
    (row: DashboardTaskGridDto) => {
      closableIdsList.push(row.requestId);
      return (
        <Checkbox
          checked={selectionList.find((c) => c === row.requestId)}
          onChange={() => {
            if (selectionList.includes(row.requestId)) {
              const index = selectionList.findIndex((e) => e === row.requestId);
              selectionList.splice(index, 1);
            } else {
              selectionList.push(row.requestId);
            }
          }}
        />
      );
    },
    [closableIdsList, selectionList]
  );

  const openRequest = React.useCallback(
    async (requestId: number, requestType: ERequestType) => {
      const subRequestId = await requestApi.getSubRequestTypeId(requestId, requestType);
      const route = await getRouteStr(requestType, subRequestId, "view");
      window.open(`#${route}`, "_blank");
    },
    [requestApi]
  );

  const columns = React.useMemo<IDataTableColumn[]>(
    () => [
      {
        header: () => <Checkbox onChange={toggleSelectAll} />,
        computed: true,
        disableSorting: true,
        fieldName: "selector",
        autoFitContent: true,
        render: columnCheckBox,
      },
      {
        header: () => t(ETLCodes.RequestNumber),
        fieldName: "requestNumber",
        render: (row: DashboardTaskGridDto) => {
          return (
            <Button
              intent={Intent.PRIMARY}
              minimal={true}
              onClick={() => openRequest(row.requestId, row.requestTypeCode)}
              text={row.requestNumber}
            />
          );
        },
      },
      {
        header: () => t(ETLCodes.RequestType),
        fieldName: "requestType",
      },
      {
        header: () => t(ETLCodes.RequestDate),
        fieldName: "requestDate",
      },
      {
        header: () => t(ETLCodes.RequestState),
        fieldName: "requestState",
      },
    ],
    [columnCheckBox, openRequest, t, toggleSelectAll]
  );

  const assignRequest = React.useCallback(
    async (requestList: number[]) => {
      if (requestList.length === 0) {
        showDialog({
          message: t(ETLCodes.SelectionEmpty),
          title: t(ETLCodes.SelectionEmpty),
          buttons: [
            {
              resultType: "yes",
              text: t(ETLCodes.General_Ok),
              intent: Intent.PRIMARY,
            },
          ],
          onConfirmed: async () => {},
        });
      } else {
        showDialog({
          message: t(ETLCodes.AssignElementsQuestion),
          title: t(ETLCodes.Assign),
          onConfirmed: async () => {
            await unassignedRequestApi.assignRequests(false, requestList);
            dispatchEvent("RefreshSearchTables");
          },
        });
      }
    },
    [showDialog, t, unassignedRequestApi, dispatchEvent]
  );

  const getCriterias = React.useCallback(() => api.getSearchCriterias(true), [api]);

  return (
    <SearchTablePage
      columns={columns}
      getCriteriasFunction={getCriterias}
      searchFunction={search}
      sortKeys={{ requestNumber: "ASC" }}
      breadCrumbs={[{ text: t(ETLCodes.Dashboard) }]}
      title={t(ETLCodes.UnassignedRequests)}
      rightElement={
        <ButtonGroup>
          <Button
            text={t(ETLCodes.Assign)}
            onClick={() => assignRequest(selectionList)}
            intent={Intent.PRIMARY}
            minimal={true}
            icon={IconNames.TICK_CIRCLE}
          />
          <ExportButton exportFunction={(sObj) => api.exportXlsFile(sObj)} sObj={sObjState} />
        </ButtonGroup>
      }
    />
  );
};
